import * as React from 'react';

import {
  AsyncBoundary,
  Button,
  Col,
  ConsoleDialog,
  ContentCard,
  ContentSection,
  Row,
  TextArea,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useAtom, useAtomValue } from 'jotai';
import {
  LucideAlertTriangle,
  LucideClock,
  LucideLock,
  LucideShieldAlert,
  LucideUnlock,
} from 'lucide-react';

import {
  $complianceHolds,
  $extendComplianceHold,
  $flagPoliceComplianceHold,
  $releaseComplianceHold,
} from '../../_state/complianceDetail.state';
import { useTranslations } from '../../i18n/useTranslations';

import { DetailField } from '~/shared/components/DetailField';

const holdStatusIcon = (status: string) => {
  switch (status) {
    case 'Active':
      return <LucideLock className="size-5 text-warning" />;
    case 'Released':
      return <LucideUnlock className="size-5 text-success" />;
    case 'Extended':
      return <LucideClock className="size-5 text-failure" />;
    case 'PoliceFlagged':
      return <LucideShieldAlert className="size-5 text-failure" />;
    default:
      return <LucideAlertTriangle className="size-5 text-neutral/40" />;
  }
};

const holdStatusColor = (status: string) => {
  switch (status) {
    case 'Active':
      return 'text-warning';
    case 'Released':
      return 'text-success';
    case 'Extended':
      return 'text-failure';
    case 'PoliceFlagged':
      return 'text-failure';
    default:
      return 'text-neutral/40';
  }
};

type ActionDialogType = 'release' | 'extend' | 'flagPolice' | null;

export const ComplianceHoldPanel = () => {
  const t = useTranslations();
  const holdsQuery = useAtomValue($complianceHolds);
  const holds = holdsQuery.data ?? [];

  const [{ isPending: isReleasePending }, releaseHold] = useAtom(
    $releaseComplianceHold,
  );
  const [{ isPending: isExtendPending }, extendHold] = useAtom(
    $extendComplianceHold,
  );
  const [{ isPending: isFlagPending }, flagPolice] = useAtom(
    $flagPoliceComplianceHold,
  );
  const toast = useToast();

  const isPending = isReleasePending || isExtendPending || isFlagPending;

  const [activeHoldId, setActiveHoldId] = React.useState<string | null>(null);
  const [actionType, setActionType] = React.useState<ActionDialogType>(null);
  const [notes, setNotes] = React.useState('');
  const [extendDays, setExtendDays] = React.useState('');

  const activeHold = holds.find((h) => h.id === activeHoldId);

  const handleOpenAction = (holdId: string, action: ActionDialogType) => {
    setActiveHoldId(holdId);
    setActionType(action);
    setNotes('');
    setExtendDays('');
  };

  const handleClose = () => {
    setActiveHoldId(null);
    setActionType(null);
  };

  const handleSubmit = async () => {
    if (!activeHold || !notes) return;
    try {
      switch (actionType) {
        case 'release':
          await releaseHold({ id: activeHold.id, notes });
          toast.success(
            t.purchaseLoadDetail.compliance.holds.messages.releaseSuccess,
          );
          break;
        case 'extend': {
          const days = parseInt(extendDays, 10);
          if (isNaN(days) || days <= 0) return;
          const newEnd = new Date(activeHold.holdEnd);
          newEnd.setUTCDate(newEnd.getUTCDate() + days);
          await extendHold({
            id: activeHold.id,
            holdEnd: newEnd.toISOString(),
            holdDays: activeHold.holdDays + days,
            notes,
          });
          toast.success(
            t.purchaseLoadDetail.compliance.holds.messages.extendSuccess,
          );
          break;
        }
        case 'flagPolice':
          await flagPolice({ id: activeHold.id, notes });
          toast.success(
            t.purchaseLoadDetail.compliance.holds.messages.flagPoliceSuccess,
          );
          break;
      }
      handleClose();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      switch (actionType) {
        case 'release':
          toast.error(
            t.purchaseLoadDetail.compliance.holds.messages.releaseError(msg),
          );
          break;
        case 'extend':
          toast.error(
            t.purchaseLoadDetail.compliance.holds.messages.extendError(msg),
          );
          break;
        case 'flagPolice':
          toast.error(
            t.purchaseLoadDetail.compliance.holds.messages.flagPoliceError(msg),
          );
          break;
      }
    }
  };

  const titleMap: Record<string, string> = {
    release: t.purchaseLoadDetail.compliance.holds.actions.release,
    extend: t.purchaseLoadDetail.compliance.holds.actions.extend,
    flagPolice: t.purchaseLoadDetail.compliance.holds.actions.flagPolice,
  };

  const canSubmit = actionType === 'extend' ? !!notes && !!extendDays : !!notes;

  return (
    <>
      <ContentCard noXPadding>
        <ContentSection
          variant="panel"
          title={t.purchaseLoadDetail.compliance.holds.title}
        >
          <AsyncBoundary
            loading={holdsQuery.loadingState?.isInitialLoading}
            error={holdsQuery.error}
          >
            {holds.length === 0 ? (
              <div className="py-8 text-center text-sm text-neutral/50">
                {t.purchaseLoadDetail.compliance.holds.empty}
              </div>
            ) : (
              <Col gap4>
                {holds.map((hold) => (
                  <Col
                    key={hold.id}
                    gap3
                    className="rounded-lg border border-neutral/10 p-4"
                  >
                    <Row className="items-center justify-between">
                      <Row gap2 className="items-center">
                        {holdStatusIcon(hold.status)}
                        <span
                          className={cn(
                            'text-sm font-semibold',
                            holdStatusColor(hold.status),
                          )}
                        >
                          {hold.status}
                        </span>
                        {hold.jurisdictionCode && (
                          <code className="rounded bg-neutral/5 px-1.5 py-0.5 text-xs">
                            {hold.jurisdictionCode}
                          </code>
                        )}
                      </Row>
                      <span className="text-xs text-neutral/40">
                        {hold.holdDays}{' '}
                        {t.purchaseLoadDetail.compliance.holds.daysLabel}
                      </span>
                    </Row>

                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                      <DetailField
                        label={
                          t.purchaseLoadDetail.compliance.holds.fields.holdStart
                        }
                        value={new Date(hold.holdStart).toLocaleDateString(
                          undefined,
                          {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          },
                        )}
                      />
                      <DetailField
                        label={
                          t.purchaseLoadDetail.compliance.holds.fields.holdEnd
                        }
                        value={new Date(hold.holdEnd).toLocaleDateString(
                          undefined,
                          {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          },
                        )}
                      />
                      <DetailField
                        label={
                          t.purchaseLoadDetail.compliance.holds.fields.maxCash
                        }
                        value={
                          hold.maxImmediateCash != null
                            ? `$${Number(hold.maxImmediateCash).toFixed(2)}`
                            : '–'
                        }
                      />
                      <DetailField
                        label={
                          t.purchaseLoadDetail.compliance.holds.fields.exemption
                        }
                        value={
                          hold.exemptionReason === 'None'
                            ? '–'
                            : hold.exemptionReason
                        }
                      />
                    </div>

                    {hold.releasedAt && (
                      <div className="text-xs text-neutral/50">
                        {t.purchaseLoadDetail.compliance.holds.releasedOn}{' '}
                        {new Date(hold.releasedAt).toLocaleString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </div>
                    )}

                    {hold.notes && (
                      <div className="text-xs text-neutral/60">
                        {hold.notes}
                      </div>
                    )}

                    {hold.status === 'Active' && (
                      <Row gap2 className="mt-1 justify-end">
                        <Button
                          size="small"
                          variant="outline"
                          color="success"
                          onPress={() => handleOpenAction(hold.id, 'release')}
                        >
                          {
                            t.purchaseLoadDetail.compliance.holds.actions
                              .release
                          }
                        </Button>
                        <Button
                          size="small"
                          variant="outline"
                          color="neutral"
                          onPress={() => handleOpenAction(hold.id, 'extend')}
                        >
                          {t.purchaseLoadDetail.compliance.holds.actions.extend}
                        </Button>
                        <Button
                          size="small"
                          variant="outline"
                          color="failure"
                          onPress={() =>
                            handleOpenAction(hold.id, 'flagPolice')
                          }
                        >
                          {
                            t.purchaseLoadDetail.compliance.holds.actions
                              .flagPolice
                          }
                        </Button>
                      </Row>
                    )}
                  </Col>
                ))}
              </Col>
            )}
          </AsyncBoundary>
        </ContentSection>
      </ContentCard>

      <ConsoleDialog
        className="@HoldActionDialog"
        title={actionType ? titleMap[actionType] : ''}
        isOpen={!!actionType}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        isLoading={isPending}
        content={
          <Col gap4>
            {actionType === 'extend' && (
              <TextField
                label={t.purchaseLoadDetail.compliance.holds.fields.extendDays}
                value={extendDays}
                onChange={setExtendDays}
                placeholder={
                  t.purchaseLoadDetail.compliance.holds.placeholders.extendDays
                }
                isRequired
              />
            )}
            <TextArea
              label={t.purchaseLoadDetail.compliance.holds.fields.notes}
              value={notes}
              onChange={setNotes}
              placeholder={
                t.purchaseLoadDetail.compliance.holds.placeholders.notes
              }
              isRequired
            />
          </Col>
        }
        footer={
          <Button
            color="primary"
            variant="solid"
            isDisabled={!canSubmit}
            isPending={isPending}
            isLoading={isPending}
            onPress={handleSubmit}
          >
            {t.purchaseLoadDetail.compliance.holds.actions.confirm}
          </Button>
        }
      />
    </>
  );
};
